import { translate } from '@core/i18n/window-language';
import { customDebounce } from '@core/utils/utils';
import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { getCurrentSearcherObj, dispatchDetailIbj } from '@core/utils/searcher';
import { updateStoreFiltersState } from '@core/utils/store';
import { ripple } from '@core/utils/ripple';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { STORE_SEARCHER_FILTERS } from '@core/constants/STORE';
import { ROUTES } from '@core/constants/ROUTES';
import {
    ACTIVE_ID_ATTRIBUTE,
    ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
    ITEM_NAME_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import { ADD_CART_ELEMENT_TYPE } from '@core/constants/CONSTANTS';
import {
    REMOVE_ACTIVE_ITEM,
    LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
    LIGHTBOX_MODAL_ATTRIBUTES,
    ELEMENT_TYPE_ADD_LIGHTBOX,
} from '@core/constants/LIGHTBOX';
import {
    TOOLTIP_POSITION_ATTRIBUTE,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

/**
 * Fill the duration of the images.
 * @param {HTMLElement} durationElement - The duration element to update
 * @param {number} duration - The duration of the images
 * @returns {void}
 */
export const fillDuration = (durationElement, duration) => {
    if (!durationElement) return;

    const durationInSec = duration / 1000;

    if (!isNaN(durationInSec)) {
        durationElement.innerHTML = `${translate('results.aprox')} ${
            Math.round(durationInSec * 100) / 100
        }s`;
    } else {
        durationElement.innerHTML = `${translate('results.aprox')} ${0}s`;
    }
};

/**
 * Fill the duration of the images from the store.
 * @param {HTMLElement} durationElement - The duration element to update
 * @returns {void}
 */
export const getFillDurationFromStore = (durationElement) => {
    if (!durationElement) return;
    durationElement.innerHTML = `${translate('results.aprox')} ${0}s`;
};

/**
 * Handle translation helper for the component.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export const handleTranslationHelper = (shadowRoot) => {
    updateUIFromShadowRoot(shadowRoot);
};

/**
 * Add ripple behavior to elements with data-ripple attribute.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export const addRippleBehavior = (shadowRoot) => {
    shadowRoot.querySelectorAll('[data-ripple]').forEach((element) => {
        ripple(element);
    });
};

/**
 * Handle report details result event.
 * @param {Object} event - The event object
 * @param {Object} component - The component instance
 * @returns {void}
 */
export const handleReportDetailsResult = (event, component) => {
    const currentFiltersStateHistory = getCurrentSearcherObj();
    const urlParams = new URLSearchParams(window.location.search);

    component._defaultImgs = (event.detail?.imgs || []).length && event.detail.imgs[0];

    if (event.detail.page === 0) {
        component._totalElements = event.detail.totalImgs;
        component._titleElement = event.detail.title;
        component._customCurrentTitle = `${component._titleElement} (${component._totalElements})`;

        if (!currentFiltersStateHistory) return;

        currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS] =
            component._totalElements;
        currentFiltersStateHistory[STORE_SEARCHER_FILTERS.REPORT_DETAILS_TITLE] =
            component._titleElement;
        updateStoreFiltersState(
            urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
            currentFiltersStateHistory,
        );

        if (event.detail.pdfFileName) {
            const pdfIcon = component.shadowRoot.querySelector('[data-report-pdf-icon]');
            if (pdfIcon) {
                pdfIcon.style.display = 'none';
                component._pdfFileName = event.detail.pdfFileName;
            }
        }
    } else {
        if (!currentFiltersStateHistory) return;

        component._customCurrentTitle = `${
            currentFiltersStateHistory[STORE_SEARCHER_FILTERS.REPORT_DETAILS_TITLE]
        } (${currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS]})`;
    }

    setTimeout(() => {
        const addLightboxIcon = component.shadowRoot.querySelector(
            '[data-add-lightbox-icon]',
        );
        if (addLightboxIcon && component._defaultImgs) {
            addLightboxIcon.setAttribute(
                DETAILS_VIEW_ATTRIBUTE_IMG,
                JSON.stringify({
                    img_src: component._defaultImgs.img_src,
                    caption: component._defaultImgs.caption,
                }),
            );
        }
    }, 0);

    component.preRender();
};

/**
 * Handle toggle tab panes event.
 * @param {Object} event - The event object
 * @param {ShadowRoot} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export const handleToggleTabPanes = (event, shadowRoot) => {
    const { tabName, dispatchEvent } = event.detail;

    shadowRoot.querySelectorAll('.tab-pane').forEach((tab) => {
        if (tab.classList.contains('active-tab-pane') && tab.id === tabName) {
            return;
        }

        tab.classList.remove('active-tab-pane');
        if (tab.id === tabName) {
            tab.classList.add('active-tab-pane');

            if (dispatchEvent) {
                dispatchDetailIbj({
                    [SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB]: tabName,
                });
            }
        }
    });
};

/**
 * Configure action buttons for the component.
 * @param {ShadowRoot} shadowRoot - The shadow root of the component
 * @param {Object} component - The component instance
 * @returns {void}
 */
export const configureActionButtons = (shadowRoot, component) => {
    const headerActionButtons = shadowRoot.querySelector(
        '[data-header-action-buttons-content]',
    );

    if (!window.location.pathname.includes(ROUTES.REPORT_DETAILS)) {
        if (headerActionButtons) {
            headerActionButtons.style.display = 'none';
        }
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (headerActionButtons) {
        headerActionButtons.style.display = 'flex';
    }

    const reportAddCart = shadowRoot.querySelector('[data-report-add-cart]');
    const deleteLightbox = shadowRoot.querySelector('[data-lightbox-delete-icon]');
    const editLightboxName = shadowRoot.querySelector('[data-lightbox-edit-icon]');
    const reportPdf = shadowRoot.querySelector('[data-report-pdf-icon]');

    if (urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)) {
        if (reportAddCart) reportAddCart.style.display = 'none';
        if (reportPdf) reportPdf.style.display = 'none';
        if (deleteLightbox) deleteLightbox.style.display = 'flex';
        if (editLightboxName) editLightboxName.style.display = 'flex';
    } else {
        if (reportAddCart) reportAddCart.style.display = 'flex';
        if (deleteLightbox) deleteLightbox.style.display = 'none';
        if (editLightboxName) editLightboxName.style.display = 'none';
    }

    const reportUid =
        urlParams.get(SEARCHER_TYPES.LIGHTBOX_ID) ||
        urlParams.get(SEARCHER_TYPES.REPORT_ID);
    const favMdIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');

    setTimeout(() => {
        [reportAddCart, favMdIcon, editLightboxName].forEach((element) => {
            if (element) {
                element.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
                element.setAttribute(
                    TOOLTIP_POSITION_ATTRIBUTE,
                    TOOLTIP_POSITION.BOTTOM_LEFT,
                );
            }
        });

        if (reportAddCart) {
            reportAddCart.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                ADD_CART_ELEMENT_TYPE.REPORT,
            );
        }

        if (favMdIcon) {
            favMdIcon.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)
                    ? ADD_CART_ELEMENT_TYPE.LIGHTBOX
                    : ADD_CART_ELEMENT_TYPE.REPORT,
            );
            favMdIcon.setAttribute(
                LIGHTBOX_MODAL_ATTRIBUTES.ELEMENT_TYPE,
                urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)
                    ? ELEMENT_TYPE_ADD_LIGHTBOX.LIGHTBOX
                    : ELEMENT_TYPE_ADD_LIGHTBOX.REPORT,
            );
        }
    }, 0);

    const downloadReportIcon = shadowRoot.querySelector('[data-report-download-icon]');

    setTimeout(() => {
        [deleteLightbox, downloadReportIcon].forEach((element) => {
            if (element) {
                element.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
            }
        });

        if (deleteLightbox) {
            deleteLightbox.setAttribute(
                LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
                REMOVE_ACTIVE_ITEM.LIGHTBOX_COLLECTION,
            );
            deleteLightbox.setAttribute(
                TOOLTIP_POSITION_ATTRIBUTE,
                TOOLTIP_POSITION.BOTTOM_LEFT,
            );
        }

        if (downloadReportIcon) {
            downloadReportIcon.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                ADD_CART_ELEMENT_TYPE.REPORT,
            );
        }

        if (editLightboxName) {
            editLightboxName.setAttribute(ITEM_NAME_ATTRIBUTE, component._titleElement);
        }
    }, 0);

    // Configure PDF icon component
    setTimeout(() => {
        if (reportPdf) {
            reportPdf.setAttribute(ACTIVE_ID_ATTRIBUTE, reportUid);
            reportPdf.setAttribute(
                TOOLTIP_POSITION_ATTRIBUTE,
                TOOLTIP_POSITION.BOTTOM_LEFT,
            );
        }
    }, 0);
};

/**
 * Create debounced event handler for better performance.
 * @param {Function} handler - The event handler function
 * @param {number} delay - The debounce delay in milliseconds
 * @returns {Function} - The debounced function
 */
export const createDebouncedHandler = (handler, delay = 100) => {
    return customDebounce(handler, delay);
};
